import React, { useMemo, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useParams, useNavigate } from "react-router-dom";
import { Lock, Loader, Eye, EyeOff } from "lucide-react";
import { validatePassword, PASSWORD_REQUIREMENTS_MESSAGE } from "@/utils/passwordValidation";
import { useAuthenticationState } from "../../../store/useAuthenticationState";

const GET_CHANGE_PASSWORD_TOKEN_STATUS = gql`
  query GetChangePasswordTokenStatus($token: String!) {
    getChangePasswordTokenStatus(token: $token) {
      valid
      username
      email
      requiresTwoFactor
      requiresTOTPEnrollment
    }
  }
`;

const CHANGE_PASSWORD_BY_TOKEN = gql`
  mutation ChangePasswordByToken($token: String!, $newPassword: String!, $code: String) {
    changePasswordByToken(token: $token, newPassword: $newPassword, code: $code) {
      message
      responseCode
      responseID
      extra
      token
      enrollmentToken
      requiresTOTPEnrollment
      pendingCompliance
    }
  }
`;

type ChangePasswordTokenStatus = {
  valid?: boolean;
  username?: string | null;
  email?: string | null;
  requiresTwoFactor?: boolean | null;
  requiresTOTPEnrollment?: boolean | null;
};

type ChangePasswordByTokenResult = {
  message?: string | null;
  responseCode?: number | null;
  responseID?: number | null;
  extra?: string | null;
  token?: string | null;
  enrollmentToken?: string | null;
  requiresTOTPEnrollment?: boolean | null;
  pendingCompliance?: boolean | null;
};

export const ChangePasswordByToken = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { setAuthenticationStateJWT } = useAuthenticationState();

  const [state, setState] = useState({
    newPassword: "",
    confirmPassword: "",
    twoFactorCode: "",
    loading: false,
    message: "",
    success: false,
    showPassword: false,
    showConfirmPassword: false
  });

  const { data, loading: statusLoading, error: statusError } = useQuery<{ getChangePasswordTokenStatus: ChangePasswordTokenStatus }>(
    GET_CHANGE_PASSWORD_TOKEN_STATUS,
    {
      variables: { token },
      skip: !token,
      fetchPolicy: "network-only"
    }
  );

  const [changePasswordByToken] = useMutation<{ changePasswordByToken: ChangePasswordByTokenResult }>(CHANGE_PASSWORD_BY_TOKEN);

  const tokenStatus = data?.getChangePasswordTokenStatus;
  const requiresTwoFactor = tokenStatus?.requiresTwoFactor === true;
  const requiresTOTPEnrollment = tokenStatus?.requiresTOTPEnrollment === true;
  const submitLabel = requiresTOTPEnrollment ? "Continue to Two-Factor Setup" : "Change Password";

  const invalidTokenMessage = useMemo(() => {
    if (!token) {
      return "The password reset token is missing or invalid.";
    }
    if (statusError) {
      return statusError.message || "The password reset token is invalid or expired.";
    }
    return "";
  }, [statusError, token]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!token) {
      setState((current) => ({ ...current, message: "Invalid or missing token", success: false }));
      return;
    }

    if (!state.newPassword || !state.confirmPassword) {
      setState((current) => ({ ...current, message: "Please fill in all required fields", success: false }));
      return;
    }

    if (requiresTwoFactor && !state.twoFactorCode.trim()) {
      setState((current) => ({ ...current, message: "Two-factor authentication code is required", success: false }));
      return;
    }

    if (state.newPassword !== state.confirmPassword) {
      setState((current) => ({ ...current, message: "Passwords do not match", success: false }));
      return;
    }

    const validation = validatePassword(state.newPassword);
    if (!validation.valid) {
      setState((current) => ({
        ...current,
        message: validation.errors.join(". "),
        success: false
      }));
      return;
    }

    setState((current) => ({ ...current, loading: true, message: "" }));

    try {
      const result = await changePasswordByToken({
        variables: {
          token,
          newPassword: state.newPassword,
          code: requiresTwoFactor ? state.twoFactorCode.trim() : null,
        },
      });

      const response = result.data?.changePasswordByToken;
      const success = response?.responseCode === 200;
      if (!success) {
        setState((current) => ({
          ...current,
          loading: false,
          message: response?.message || "Unable to change password",
          success: false
        }));
        return;
      }

      if (response?.requiresTOTPEnrollment && response?.token) {
        setAuthenticationStateJWT('');
        setState((current) => ({
          ...current,
          loading: false,
          message: "Password changed successfully. Please sign in to continue two-factor setup.",
          success: true
        }));
        setTimeout(() => {
          navigate('/login');
        }, 2000);
        return;
      }

      setState((current) => ({
        ...current,
        loading: false,
        message: response?.message || "Password changed successfully",
        success: true
      }));

      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error: any) {
      setState((current) => ({
        ...current,
        loading: false,
        message: error.message || "An error occurred. Please try again.",
        success: false
      }));
    }
  };

  const getPasswordStrengthColor = (password: string) => {
    const validation = validatePassword(password);
    if (!password) return "border-gray-300";
    return validation.valid ? "border-green-500" : "border-red-300";
  };

  if (!token || invalidTokenMessage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-full max-w-md space-y-8 bg-white p-8 rounded-lg shadow-md text-center">
          <h2 className="text-2xl font-bold text-red-600">Invalid Token</h2>
          <p className="text-gray-600">{invalidTokenMessage || "The password reset token is missing or invalid."}</p>
          <a
            href="/login"
            className="inline-block text-blue-600 hover:text-blue-500"
          >
            Return to Login
          </a>
        </div>
      </div>
    );
  }

  if (statusLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-full max-w-md space-y-4 bg-white p-8 rounded-lg shadow-md text-center">
          <Loader className="mx-auto h-6 w-6 animate-spin text-blue-600" />
          <p className="text-gray-600">Validating your password setup link...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="w-full max-w-md space-y-8 bg-white p-8 rounded-lg shadow-md">
        <div className="flex justify-center">
          <img
            src={`${process.env.PUBLIC_URL}/logoODR.png`}
            alt="logo"
            className="h-20 w-auto"
          />
        </div>

        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">
            Set New Password
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {requiresTOTPEnrollment
              ? "Set your password first. Diversion Database will guide you through two-factor setup next."
              : "Enter your new password below"}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label htmlFor="change-password-token-new" className="block text-sm font-medium text-gray-700">
              New Password
            </label>
            <div className={`flex items-center border rounded-md ${getPasswordStrengthColor(state.newPassword)}`}>
              <Lock className="h-5 w-5 text-gray-400 mx-3" />
              <input
                id="change-password-token-new"
                type={state.showPassword ? "text" : "password"}
                value={state.newPassword}
                onChange={(e) => setState((current) => ({ ...current, newPassword: e.target.value }))}
                className="w-full py-2 border-0 focus:ring-0 text-base"
                style={{ margin: 0, padding: "8px 0 8px 10px" }}
                placeholder="Enter new password"
                disabled={state.loading}
              />
              <button
                type="button"
                onClick={() => setState((current) => ({ ...current, showPassword: !current.showPassword }))}
                className="mx-3 text-gray-400 hover:text-gray-600"
              >
                {state.showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            <div className="text-xs text-gray-500">
              {PASSWORD_REQUIREMENTS_MESSAGE}
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="change-password-token-confirm" className="block text-sm font-medium text-gray-700">
              Confirm Password
            </label>
            <div className={`flex items-center border rounded-md ${
              state.confirmPassword && state.newPassword === state.confirmPassword
                ? "border-green-500"
                : state.confirmPassword
                  ? "border-red-300"
                  : "border-gray-300"
            }`}>
              <Lock className="h-5 w-5 text-gray-400 mx-3" />
              <input
                id="change-password-token-confirm"
                type={state.showConfirmPassword ? "text" : "password"}
                value={state.confirmPassword}
                onChange={(e) => setState((current) => ({ ...current, confirmPassword: e.target.value }))}
                className="w-full py-2 border-0 focus:ring-0 text-base"
                style={{ margin: 0, padding: "8px 0 8px 10px" }}
                placeholder="Confirm new password"
                disabled={state.loading}
              />
              <button
                type="button"
                onClick={() => setState((current) => ({ ...current, showConfirmPassword: !current.showConfirmPassword }))}
                className="mx-3 text-gray-400 hover:text-gray-600"
              >
                {state.showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {requiresTwoFactor && (
            <div className="space-y-2">
              <label htmlFor="change-password-token-twofactor" className="block text-sm font-medium text-gray-700">
                Two-Factor Code
              </label>
              <input
                id="change-password-token-twofactor"
                type="text"
                value={state.twoFactorCode}
                onChange={(e) => setState((current) => ({ ...current, twoFactorCode: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter code from authenticator app"
                disabled={state.loading}
              />
            </div>
          )}

          {state.message && (
            <div className={`text-sm text-center p-3 rounded-md ${
              state.success
                ? "bg-green-50 text-green-800 border border-green-200"
                : "bg-red-50 text-red-800 border border-red-200"
            }`}>
              {state.message}
              {state.success && (
                <div className="mt-1 text-xs">Redirecting to login...</div>
              )}
            </div>
          )}

          {!state.success && (
            <button
              type="submit"
              disabled={state.loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {state.loading ? (
                <Loader className="h-5 w-5 animate-spin" />
              ) : (
                submitLabel
              )}
            </button>
          )}
        </form>

        <div className="text-center">
          <a
            href="/login"
            className="text-sm text-blue-600 hover:text-blue-500"
          >
            Back to Login
          </a>
        </div>
      </div>
    </div>
  );
};
